/**
 * features/expenses/expensesWrite.ts
 * Expense writes. Input VAT stays dormant (0) while the company is
 * unregistered, so total === amount; the field is kept for when VAT
 * is switched on.
 */
import { collection, deleteDoc, deleteField, doc } from 'firebase/firestore';
import { db, addDoc, updateDoc } from '@/lib/firebase';
import { round2 } from '@/lib/money';
import { COLLECTIONS, type ExpenseCategory, type PaymentMethod } from '@/types';

export interface ExpensePayload {
  date: string;
  category: ExpenseCategory;
  payee: string;
  description: string;
  amount: number;
  method: PaymentMethod;
  reference?: string;
  notes?: string;
  attachmentUrl?: string;
}

function base(p: ExpensePayload) {
  const amount = round2(p.amount);
  const data: Record<string, unknown> = {
    date: p.date,
    category: p.category,
    payee: p.payee.trim(),
    description: p.description.trim(),
    amount,
    vatAmount: 0,
    total: amount,
    method: p.method,
    updatedAt: new Date().toISOString(),
  };
  const ref = p.reference?.trim();
  if (ref) data.reference = ref;
  const notes = p.notes?.trim();
  if (notes) data.notes = notes;
  if (p.attachmentUrl) data.attachmentUrl = p.attachmentUrl;
  return data;
}

export async function addExpense(p: ExpensePayload, actor: string): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTIONS.expenses), {
    ...base(p),
    createdAt: new Date().toISOString(),
    createdBy: actor,
  });
  return ref.id;
}

export async function updateExpense(id: string, p: ExpensePayload): Promise<void> {
  const data = base(p);
  if (!p.attachmentUrl) data.attachmentUrl = deleteField();
  await updateDoc(doc(db, COLLECTIONS.expenses, id), data);
}

export async function deleteExpense(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.expenses, id));
}
